import { isPreContractPanelAllowed } from './preContractModuleLayout';
import type {
    PreContractModuleCell,
    PreContractRoofModuleLayout,
} from './preContractTypes';

const FULL_MODULE_LAYOUTS_KEY = 'preContractFullModuleLayouts.v1';

export function countPresentModuleCells(layouts: PreContractRoofModuleLayout[]): number {
    let count = 0;
    for (const layout of layouts) {
        for (const cell of layout.modules) {
            if (cell) count += 1;
        }
    }
    return count;
}

/** Keep the largest max-fill catalog seen this session (includes grey/purple panels). */
export function persistPreContractFullModuleLayouts(
    layouts: PreContractRoofModuleLayout[],
    options?: { force?: boolean },
): void {
    const nextCount = countPresentModuleCells(layouts);
    if (nextCount === 0) return;
    try {
        const existing = loadPreContractFullModuleLayouts();
        const existingCount = existing ? countPresentModuleCells(existing) : 0;
        // Never shrink the catalog — a trimmed live publish must not replace max-fill.
        if (existingCount > nextCount) return;
        if (!options?.force && existingCount >= nextCount) return;
        sessionStorage.setItem(FULL_MODULE_LAYOUTS_KEY, JSON.stringify(layouts));
    } catch {
        /* ignore */
    }
}

export function loadPreContractFullModuleLayouts(): PreContractRoofModuleLayout[] | null {
    try {
        const raw = sessionStorage.getItem(FULL_MODULE_LAYOUTS_KEY);
        if (!raw) return null;
        const parsed = JSON.parse(raw) as PreContractRoofModuleLayout[];
        if (!Array.isArray(parsed) || countPresentModuleCells(parsed) === 0) return null;
        return parsed;
    } catch {
        return null;
    }
}

export function clearPreContractFullModuleLayouts(): void {
    try {
        sessionStorage.removeItem(FULL_MODULE_LAYOUTS_KEY);
    } catch {
        /* ignore */
    }
}

function layoutMergeKey(layout: PreContractRoofModuleLayout): string {
    if (typeof layout.roofTagId === 'number' && Number.isFinite(layout.roofTagId)) {
        return `tag:${layout.roofTagId}`;
    }
    return `idx:${layout.roofIndex}`;
}

function moduleCellKey(cell: NonNullable<PreContractModuleCell>): string {
    const id = String(cell.id ?? '').trim().toUpperCase();
    if (id) return id;
    return String(cell.meshId ?? '').trim().toUpperCase();
}

function overlayMaxFillCell(
    catalog: NonNullable<PreContractModuleCell>,
    live: NonNullable<PreContractModuleCell> | null,
    includeAll: boolean,
    includeGreyPanels: boolean,
    includePurplePanels: boolean,
): NonNullable<PreContractModuleCell> {
    const solarAccess = live && live.solarAccess > 0 ? live.solarAccess : catalog.solarAccess;
    const allowed = isPreContractPanelAllowed(solarAccess, includeGreyPanels, includePurplePanels);
    const included = allowed && (includeAll || (live?.included ?? catalog.included));
    return {
        ...catalog,
        ...live,
        id: catalog.id || live?.id || catalog.id,
        solarAccess,
        included,
        productionKwh:
            typeof live?.productionKwh === 'number' && live.productionKwh > 0
                ? live.productionKwh
                : catalog.productionKwh,
        multiplier:
            typeof live?.multiplier === 'number' && live.multiplier > 0
                ? live.multiplier
                : catalog.multiplier,
        shadeColor: live?.shadeColor ?? catalog.shadeColor,
    };
}

/**
 * AI Config always starts from the stored max-fill catalog so trimmed applied
 * designs do not empty unused roofs. When the current board is still a subset,
 * every allowed panel is included.
 */
export function expandLayoutsToMaxFill(
    current: PreContractRoofModuleLayout[],
    catalog: PreContractRoofModuleLayout[] | null | undefined,
    options?: {
        includeGreyPanels?: boolean;
        includePurplePanels?: boolean;
    },
): PreContractRoofModuleLayout[] {
    const includeGreyPanels = options?.includeGreyPanels !== false;
    const includePurplePanels = options?.includePurplePanels !== false;
    const baseline = catalog?.length ? catalog : current;
    if (!baseline.length) return current;

    const includeAll = countPresentModuleCells(current) < countPresentModuleCells(baseline);
    const currentByKey = new Map<string, PreContractRoofModuleLayout>();
    current.forEach((layout) => {
        currentByKey.set(layoutMergeKey(layout), layout);
        currentByKey.set(`idx:${layout.roofIndex}`, layout);
    });

    const usedCurrent = new Set<PreContractRoofModuleLayout>();
    const expanded = baseline.map((layout, index) => {
        const live =
            currentByKey.get(layoutMergeKey(layout)) ??
            currentByKey.get(`idx:${layout.roofIndex}`) ??
            current[index] ??
            null;
        if (live) usedCurrent.add(live);
        const liveById = new Map<string, NonNullable<PreContractModuleCell>>();
        live?.modules.forEach((cell) => {
            if (!cell) return;
            const key = moduleCellKey(cell);
            if (key) liveById.set(key, cell);
        });
        return {
            ...layout,
            cols: layout.cols || live?.cols || layout.cols,
            moduleOrientation: layout.moduleOrientation ?? live?.moduleOrientation,
            modules: layout.modules.map((cell, cellIndex) => {
                if (!cell) return null;
                const key = moduleCellKey(cell);
                const overlay =
                    (key ? liveById.get(key) : undefined) ??
                    live?.modules[cellIndex] ??
                    null;
                return overlayMaxFillCell(
                    cell,
                    overlay,
                    includeAll,
                    includeGreyPanels,
                    includePurplePanels,
                );
            }),
        };
    });

    current.forEach((layout) => {
        if (usedCurrent.has(layout)) return;
        if (!layout.modules.some((cell) => cell != null)) return;
        expanded.push({
            ...layout,
            modules: layout.modules.map((cell) => {
                if (!cell) return null;
                return overlayMaxFillCell(
                    cell,
                    null,
                    includeAll,
                    includeGreyPanels,
                    includePurplePanels,
                );
            }),
        });
    });

    return expanded;
}
